import {
  CreditRedemptionViewModel,
  DEPRECATED_PRODUCT_CODES,
  FREE_PRODUCT_CODES,
  JS24_PRODUCT_CODES,
  ProductCode,
  productInformationRegistry,
  ProductPlatformGroup,
  SlotCode,
  UPGRADEABLE_PRODUCT_CODES,
} from '@mkp/shared/models';

export const isFreeProductCode = (
  productCode: ProductCode | SlotCode
): productCode is ProductCode.JOBSOFFER22FREE | ProductCode.JS24FREE | ProductCode.JOBUP24FREE => {
  return (FREE_PRODUCT_CODES as readonly ProductCode[]).includes(productCode as ProductCode);
};

/**
 * Checks if a product code is deprecated and should not be displayed to users.
 * Uses the canonical DEPRECATED_PRODUCT_CODES list as the single source of truth.
 */
export const isDeprecatedProductCode = (productCode: ProductCode): boolean =>
  DEPRECATED_PRODUCT_CODES.includes(productCode);

/**
 * Checks if a product code is an offline product that is not available in ecom.
 * Offline products are defined in productInformationRegistry and should not trigger API calls to ecom.
 * These products include: Pay Per Performance, Spontaneous Applications, Offsite, and Gifted Free products.
 */
export const isOfflineProductCode = (productCode: ProductCode): boolean =>
  productCode in productInformationRegistry;

export const canProductBeUpgraded = (
  creditRedeem: CreditRedemptionViewModel | undefined
): boolean => {
  const productCode = creditRedeem?.information?.productCode as ProductCode;
  return UPGRADEABLE_PRODUCT_CODES.includes(productCode);
};

export const isJs24ProductCode = (
  productType: ProductCode | SlotCode
): productType is ProductCode => JS24_PRODUCT_CODES.includes(productType as ProductCode);

/**
 * Gets the product platform group for a product code or slot code
 * If the type is a SlotCode, maps it to the corresponding ProductCode platform
 */
export const getProductPlatformGroup = (
  type: ProductCode | SlotCode
): ProductPlatformGroup | null => {
  const normalizedType = type.startsWith('SLOT') ? (type.slice(4) as ProductCode) : type;

  if (normalizedType.startsWith('JOBUP')) return ProductPlatformGroup.JobUp;
  if (normalizedType.startsWith('JS24')) return ProductPlatformGroup.JobScout24;
  if (type === ProductCode.PAYPERPERFORMANCE) return null;

  return ProductPlatformGroup.Jobs;
};
